<script setup lang="ts">
import gsap from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'
import { toHead } from 'vue-datocms'
import { queryProject } from '~/graphql/queries'
import type { StringMultiLocaleField, BooleanTypeNonNullMultiLocaleField } from '~/types/datocms'
import { ApiErrorType } from '~/types'
import type { SectionComponents } from '~/types/components'
import { theme } from '#tailwind-config'

const { throwError, handleApiError } = useApiError()
const { resolveSectionComponent } = useResolveComponent()

const breakpoints = useBreakpoints(theme.screens)
const isScreenLg = breakpoints.greater('lg')

const appStore = useAppStore()
const { locale } = useI18n()
const route = useRoute()

const { data, error } = await useDatoQuery(queryProject, {
  variables: {
    locale: locale.value,
    slug: route.params.slug as string
  }
})

if (error.value) {
  handleApiError(error.value)
}

if (!data.value?.project) {
  throwError(ApiErrorType.NOT_FOUND)
}

// Dynamic route parameters
const setI18nParams = useSetI18nParams()
const publishedLocales = useLocaleSlugs(
  data.value?.project?._allSlugLocales as StringMultiLocaleField[],
  data.value?.project?._allPublishTranslationLocales as BooleanTypeNonNullMultiLocaleField[]
)
setI18nParams(publishedLocales)

if (data.value?.project?.seoMetadata?.noIndex || !data.value?.project?.publishTranslation) {
  useRobotsRule('noindex, nofollow')
}

useHead(() => {
  return toHead(data.value?.project?._seoMetaTags || [])
})

const textRef = ref<HTMLElement | null>(null)
const sectionRef = ref<HTMLElement | null>(null)
const entered = ref(false)

let ctx: gsap.Context | null = null

onMounted(() => {
  appStore.setTheme('light')
  appStore.setGradientHeader('black')
  appStore.setShowFooter(false)

  gsap.registerPlugin(ScrollTrigger)

  if (sectionRef.value && textRef.value && isScreenLg.value) {
    ctx = gsap.context(() => {
      gsap.fromTo(
        textRef.value,
        { y: 0 },
        {
          y: 0,
          scrollTrigger: {
            trigger: sectionRef.value,
            start: 'top top',
            end: 'bottom center',
            pin: textRef.value,
            pinSpacing: false,
            scrub: true,
            onUpdate: (self) => {
              if (self.progress > 0.1 && !entered.value) {
                entered.value = true
              } else if (self.progress < 0.1 && entered.value) {
                entered.value = false
              }
            }
          }
        }
      )

      ScrollTrigger.refresh()
    }, sectionRef.value)
  }
})

const { emitterEmit } = useEmitter()
const videoBlockRef = ref<HTMLElement | null>(null)
const { isOutside } = useMouseInElement(videoBlockRef)

let triggeredOnce = false

watch(isOutside, (outside) => {
  if (!outside && !triggeredOnce) {
    triggeredOnce = true
    emitterEmit('hotspotEnter', {
      label: 'Play trailer',
      iconLeft: 'custom:play-small'
    })
  }
})

onBeforeUnmount(() => {
  appStore.setShowFooter(true)
  emitterEmit('hotspotLeave')

  if (ctx) {
    ctx.revert()
  }
})
</script>

<template>
  <article>
    <div class="bg-gradient-dark overflow-hidden">
      <div
        ref="sectionRef"
        class="relative h-dvh w-screen flex-col gap-10 text-center lg:flex lg:h-auto lg:w-full lg:px-8 lg:pb-10 lg:pt-[220px]"
      >
        <div
          ref="textRef"
          class="transition-colors duration-300"
          :class="entered ? 'text-black' : 'text-yellow'"
        >
          <Heading
            v-if="data?.project?.title"
            :level="1"
            :size="2"
            class="hidden font-light lg:block"
            :is-semantic="true"
            >{{ data?.project?.title }}</Heading
          >
        </div>
        <div
          v-if="
            data.project?.video && (data.project.video?.thumbnail || data.project.video?.videoLoop)
          "
          ref="videoBlockRef"
          class="relative cursor-none"
          @mouseover="
            data.project?.video?.video
              ? emitterEmit('hotspotEnter', {
                  label: 'Play trailer',
                  iconLeft: 'custom:play-small'
                })
              : undefined
          "
          @mouseleave="emitterEmit('hotspotLeave')"
        >
          <div
            class="pointer-events-none absolute left-0 top-0 z-[3] h-full w-full bg-black/30 object-cover"
          ></div>
          <VideoBlock
            :data="data.project?.video"
            @mouseover="
              data.project?.video?.video
                ? emitterEmit('hotspotEnter', {
                    label: 'Play trailer',
                    iconLeft: 'custom:play-small'
                  })
                : undefined
            "
            @mouseleave="emitterEmit('hotspotLeave')"
          />
        </div>
        <span
          v-if="data.project?.video?.video"
          class="pointer-events-none absolute bottom-4 left-4 z-[4] inline-flex items-center gap-4 rounded-full bg-black/60 px-4 py-2 uppercase text-yellow lg:hidden"
        >
          <Icon name="custom:play-small" size="16" />
          Play trailer
        </span>
      </div>
    </div>
    <div class="relative z-2 bg-black pt-8 text-yellow lg:pt-14">
      <div class="container grid-default pb-20 lg:pb-36">
        <div class="col-span-full flex flex-col lg:col-span-4">
          <span v-if="data?.project?.title" class="text-sm uppercase text-cream"
            >Original title</span
          >
          <span v-if="data?.project?.title" class="text-2xl uppercase">{{
            data?.project?.title
          }}</span>
          <div v-if="data.project?.tags" class="flex flex-wrap gap-1">
            <span
              v-for="(item, index) in data.project?.tags"
              :key="index"
              class="mt-2 rounded-[32px] border border-grey px-3 py-2 text-sm uppercase text-cream"
            >
              {{ item }}
            </span>
          </div>
          <div class="mt-10 grid grid-cols-2 gap-x-4 gap-y-6 lg:hidden lg:gap-y-8">
            <div v-for="(item, key) in data.project?.metadata" :key="key" class="flex flex-col">
              <span class="text-sm uppercase text-cream">{{ key }}</span>
              <span
                v-for="value in item.split(',')"
                :key="value"
                class="mt-2 block text-lg uppercase lg:text-2xl"
              >
                {{ value }}
              </span>
            </div>
          </div>
          <div class="mt-12 flex flex-col gap-2 lg:mt-20">
            <span class="text-sm uppercase text-cream">Official poster</span>
            <Image
              v-if="data?.project?.poster"
              class="w-full max-w-[164px] object-cover lg:max-w-[448px]"
              loading="lazy"
              :src="
                data?.project?.poster.responsiveImage
                  ? data?.project?.poster.responsiveImage?.src
                  : data?.project?.poster.url
              "
              :srcset="
                data?.project?.poster.responsiveImage
                  ? data?.project?.poster.responsiveImage?.webpSrcSet
                  : undefined
              "
              :width="
                data?.project?.poster.responsiveImage
                  ? data?.project?.poster.responsiveImage?.width
                  : (data?.project?.poster.width ?? 1280)
              "
              :height="
                data?.project?.poster.responsiveImage
                  ? data?.project?.poster.responsiveImage?.height
                  : (data?.project?.poster.height ?? 720)
              "
              :alt="data?.project?.poster.alt ?? ''"
              sizes="(min-width: 1400px) 1280px, 93.7vw"
            />
          </div>
        </div>
        <div class="col-span-full flex h-full flex-col lg:col-span-5 lg:col-start-8">
          <div class="hidden grid-cols-2 gap-x-4 gap-y-6 lg:grid lg:gap-y-8">
            <div v-for="(item, key) in data.project?.metadata" :key="key" class="flex flex-col">
              <span class="text-sm uppercase text-cream">{{ key }}</span>
              <span
                v-for="value in item.split(',')"
                :key="value"
                class="mt-2 block text-lg uppercase lg:text-2xl"
              >
                {{ value }}
              </span>
            </div>
          </div>
          <div>
            <div
              v-if="data.project?.description"
              class="mb-2 mt-12 text-sm uppercase text-cream lg:mt-30"
            >
              Description
            </div>
            <div
              v-if="data.project?.description"
              class="text-base lg:text-lg"
              v-html="data.project?.description"
            />
            <div v-if="data.project?.html" class="mb-2 mt-12 text-sm uppercase text-cream">
              Behind the scenes
            </div>
            <div
              v-if="data.project?.html"
              class="text-base lg:text-lg"
              v-html="data.project?.html"
            />
          </div>
        </div>
      </div>
      <div v-if="Array.isArray(data?.project?.blocks) && data?.project?.blocks.length">
        <component
          :is="resolveSectionComponent(section.__typename as SectionComponents)"
          v-for="(section, idx) in data.project.blocks"
          :key="section.__typename"
          :module-index="idx + 1"
          :data="section"
        />
      </div>
      <ProjectNext
        class="mx-auto max-w-[912px] px-4 2xl:max-w-[1556px]"
        :current-record-slug="data.project.slug"
        :all-records="data.allProjects"
        :all-records-count="data._allProjectsMeta.count"
      />
    </div>
  </article>
</template>
